import { useMemo, useState } from "react";
import { useParams } from "react-router";
import {
  addDays,
  format,
  isSameDay,
  startOfDay,
  subDays,
} from "date-fns";
import { zhCN } from "date-fns/locale";
import { CalendarClock, CheckCircle2, CircleDashed, Loader } from "lucide-react";
import { AdminPageHeader } from "@/components/shell/AdminPage";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { useAccountWorkspace } from "@/lib/account-workspace";
import { cn } from "@/lib/utils";

type OpsStatus = "done" | "active" | "planned";

type OpsTask = {
  id: string;
  title: string;
  detail: string;
  date: Date;
  status: OpsStatus;
};

const STATUS_META: Record<
  OpsStatus,
  { label: string; icon: typeof CheckCircle2; badge: "default" | "secondary" | "outline" }
> = {
  done: { label: "完成", icon: CheckCircle2, badge: "secondary" },
  active: { label: "进行中", icon: Loader, badge: "default" },
  planned: { label: "计划中", icon: CircleDashed, badge: "outline" },
};

/** Demo timeline relative to "today" — replace with API when ops plan ships. */
function buildMockTasks(today: Date): OpsTask[] {
  return [
    {
      id: "1",
      title: "公众号周报 · 品牌动态复盘",
      detail: "汇总上周触达与互动，沉淀可复用素材要点。",
      date: subDays(today, 7),
      status: "done",
    },
    {
      id: "2",
      title: "小红书种草笔记发布",
      detail: "围绕核心卖点发布 2 篇图文，并回填素材库。",
      date: subDays(today, 3),
      status: "done",
    },
    {
      id: "3",
      title: "官网落地页文案迭代",
      detail: "根据本周咨询反馈调整首屏与 CTA。",
      date: today,
      status: "active",
    },
    {
      id: "4",
      title: "竞品监测周报",
      detail: "采集竞品渠道更新，写入品牌知识库要点。",
      date: addDays(today, 2),
      status: "planned",
    },
    {
      id: "5",
      title: "抖音短视频拍摄日",
      detail: "脚本已定；拍摄 + 粗剪，成片进素材库。",
      date: addDays(today, 5),
      status: "planned",
    },
    {
      id: "6",
      title: "月度运营复盘会",
      detail: "对齐渠道点亮进度与下月内容日历。",
      date: addDays(today, 12),
      status: "planned",
    },
  ];
}

export default function OpsPlanPlaceholderPage() {
  const { brandId } = useParams();
  const { brands } = useAccountWorkspace();
  const brand = brands.find((item) => String(item.id) === brandId);
  const today = useMemo(() => startOfDay(new Date()), []);
  const tasks = useMemo(() => buildMockTasks(today), [today]);
  const [selected, setSelected] = useState<Date | undefined>(today);

  const taskDates = useMemo(() => tasks.map((task) => task.date), [tasks]);
  const selectedTasks = useMemo(() => {
    if (!selected) return tasks;
    const matched = tasks.filter((task) => isSameDay(task.date, selected));
    return matched.length > 0 ? matched : tasks;
  }, [selected, tasks]);
  const showingDayFilter =
    selected != null && tasks.some((task) => isSameDay(task.date, selected));

  return (
    <div className="w-full space-y-6">
      <AdminPageHeader
        title="运营计划"
        description={
          brand
            ? `查看「${brand.name}」过去与未来的执行计划与状态。当前为演示数据，正式排期能力即将开放。`
            : "查看品牌过去与未来的执行计划与状态。当前为演示数据。"
        }
      />

      <div className="grid gap-6 lg:grid-cols-[auto_1fr]">
        <section className="h-fit rounded-lg border border-border bg-card p-3 shadow-sm">
          <div className="mb-2 flex items-center gap-2 px-1 text-sm font-medium">
            <CalendarClock className="size-4 text-muted-foreground" />
            执行日历
          </div>
          <Calendar
            mode="single"
            locale={zhCN}
            selected={selected}
            onSelect={setSelected}
            defaultMonth={today}
            modifiers={{ hasTask: taskDates }}
            modifiersClassNames={{
              hasTask:
                "relative after:absolute after:bottom-1 after:left-1/2 after:size-1 after:-translate-x-1/2 after:rounded-full after:bg-primary",
            }}
            className="rounded-md"
          />
          <p className="mt-2 px-1 text-xs text-muted-foreground">
            圆点标记有任务的日期；点击可筛选时间线。
          </p>
        </section>

        <section className="min-w-0 rounded-lg border border-border bg-card shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-5 py-3">
            <div>
              <h2 className="text-sm font-medium">执行时间线</h2>
              <p className="text-xs text-muted-foreground">
                {showingDayFilter && selected
                  ? format(selected, "yyyy年M月d日 EEEE", { locale: zhCN })
                  : "全部计划（含已完成与即将执行）"}
              </p>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(Object.keys(STATUS_META) as OpsStatus[]).map((status) => (
                <Badge key={status} variant={STATUS_META[status].badge}>
                  {STATUS_META[status].label}
                </Badge>
              ))}
            </div>
          </div>

          <ScrollArea className="h-[min(28rem,60vh)]">
            <ol className="relative space-y-0 px-5 py-4">
              {selectedTasks.map((task, index) => {
                const meta = STATUS_META[task.status];
                const Icon = meta.icon;
                const isLast = index === selectedTasks.length - 1;
                return (
                  <li key={task.id} className="relative flex gap-4 pb-6 last:pb-1">
                    {!isLast ? (
                      <span
                        className="absolute top-6 bottom-0 left-[11px] w-px bg-border"
                        aria-hidden
                      />
                    ) : null}
                    <div
                      className={cn(
                        "relative z-10 mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full border bg-background",
                        task.status === "done" && "border-muted-foreground/30 text-muted-foreground",
                        task.status === "active" && "border-primary text-primary",
                        task.status === "planned" && "border-border text-muted-foreground",
                      )}
                    >
                      <Icon className={cn("size-3.5", task.status === "active" && "animate-spin")} />
                    </div>
                    <div className="min-w-0 flex-1 space-y-1.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <time
                          dateTime={task.date.toISOString()}
                          className="text-xs tabular-nums text-muted-foreground"
                        >
                          {format(task.date, "M月d日 EEE", { locale: zhCN })}
                        </time>
                        <Badge variant={meta.badge}>{meta.label}</Badge>
                      </div>
                      <div className="text-sm font-medium leading-snug">{task.title}</div>
                      <p className="text-sm text-muted-foreground">{task.detail}</p>
                    </div>
                  </li>
                );
              })}
            </ol>
          </ScrollArea>

          <Separator />
          <p className="px-5 py-3 text-xs text-muted-foreground">
            占位页：正式版将支持排期、执行状态回写与渠道联动。
          </p>
        </section>
      </div>
    </div>
  );
}
